import { Award, ChevronDown, Medal, Sparkles, Star, Trophy } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Dialog, EmptyState, PageHeader, SearchField } from '../components/ui';
import { useApp } from '../domain/AppContext';
import { compareClassNames, formatClassName } from '../domain/classCode';
import type { DemoState, ResultRecord, Student } from '../domain/types';

type HighlightFilter = 'all' | 'medalists' | 'qualified';
type HighlightEntry = {
  result: ResultRecord;
  student: Student;
  olympiad: DemoState['olympiads'][number];
  phase: DemoState['olympiads'][number]['phases'][number] | undefined;
};

const highlightCategories = [
  { id: 'multi', label: 'Multimedalhistas', title: 'Legado de Conquistas' },
  { id: 'diamond', label: 'Diamante', title: 'Elite Diamante' },
  { id: 'platinum', label: 'Platina', title: 'Destaque Platina' },
  { id: 'gold', label: 'Ouro', title: 'Excelência Dourada' },
  { id: 'silver', label: 'Prata', title: 'Ascensão Prateada' },
  { id: 'bronze', label: 'Bronze', title: 'Conquista de Bronze' },
  { id: 'honor', label: 'Honra ao Mérito', title: 'Reconhecimento de Excelência' },
  { id: 'qualified', label: 'Próxima fase', title: 'Talentos em Ascensão' },
  { id: 'other', label: 'Outras premiações', title: 'Outras Conquistas' },
] as const;

export function HighlightsWallPage() {
  const { profile, data } = useApp();
  const [typeFilter, setTypeFilter] = useState<HighlightFilter>('all');
  const [medalFilter, setMedalFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [olympiadFilter, setOlympiadFilter] = useState('all');
  const [classFilter, setClassFilter] = useState('all');
  const [selectedMultiStudentId, setSelectedMultiStudentId] = useState<string | null>(null);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(() => new Set());

  const highlights = useMemo(() => {
    if (!profile || !data) return [];
    const normalized = query.trim().toLocaleLowerCase('pt-BR');

    const persistedHighlights = data.results
      .filter((result) => result.published && (Boolean(result.award.trim()) || result.qualifiedNextPhase))
      .map((result) => {
        const student = data.students.find((item) => item.id === result.studentId);
        const olympiad = data.olympiads.find((item) => item.id === result.olympiadId);
        const phase = olympiad?.phases.find((item) => item.id === result.phaseId);
        return student && olympiad ? { result, student, olympiad, phase } : null;
      })
      .filter((item): item is HighlightEntry => Boolean(item));

    const medalCounts = persistedHighlights.filter((item) => item.result.award.trim()).reduce((counts, item) => counts.set(item.student.id, (counts.get(item.student.id) ?? 0) + 1), new Map<string, number>());

    return persistedHighlights
      .filter(({ result }) => typeFilter === 'all' || (typeFilter === 'medalists' ? Boolean(result.award.trim()) : result.qualifiedNextPhase))
      .filter(({ result, student }) => {
        if (medalFilter === 'all') return true;
        if (medalFilter === 'multi') return (medalCounts.get(student.id) ?? 0) > 1 && Boolean(result.award.trim());
        return getAwardCategoryId(result.award, result.qualifiedNextPhase) === medalFilter;
      })
      .filter(({ olympiad }) => olympiadFilter === 'all' || olympiad.id === olympiadFilter)
      .filter(({ student }) => classFilter === 'all' || student.className === classFilter)
      .filter(({ result, student, olympiad }) => !normalized || `${student.name} ${student.className} ${formatClassName(student.className)} ${olympiad.name} ${olympiad.acronym} ${result.award} ${result.classification}`.toLocaleLowerCase('pt-BR').includes(normalized))
      .sort((a, b) => compareClassNames(a.student.className, b.student.className) || a.student.name.localeCompare(b.student.name, 'pt-BR'));
  }, [classFilter, data, medalFilter, olympiadFilter, profile, query, typeFilter]);

  if (!profile || !data) return null;

  const publishedHighlights = data.results.filter((item) => item.published && (item.award.trim() || item.qualifiedNextPhase));
  const highlightedOlympiadIds = new Set(publishedHighlights.map((item) => item.olympiadId));
  const highlightedStudentIds = new Set(publishedHighlights.map((item) => item.studentId));
  const olympiadOptions = data.olympiads.filter((item) => highlightedOlympiadIds.has(item.id)).sort((a, b) => a.acronym.localeCompare(b.acronym, 'pt-BR'));
  const classOptions = [...new Set(data.students.filter((item) => highlightedStudentIds.has(item.id)).map((item) => item.className).filter(Boolean))].sort(compareClassNames);
  const medalCountByStudent = publishedHighlights.filter((item) => item.award.trim()).reduce((counts, item) => counts.set(item.studentId, (counts.get(item.studentId) ?? 0) + 1), new Map<string, number>());
  const grouped = highlights.reduce((groups, item) => {
    const awardCategoryId = getAwardCategoryId(item.result.award, item.result.qualifiedNextPhase);
    const awardCategory = groups.get(awardCategoryId) ?? [];
    awardCategory.push(item);
    groups.set(awardCategoryId, awardCategory);
    if (item.result.award.trim() && (medalCountByStudent.get(item.student.id) ?? 0) > 1) {
      const multiCategory = groups.get('multi') ?? [];
      if (!multiCategory.some((entry) => entry.student.id === item.student.id)) {
        multiCategory.push(item);
        groups.set('multi', multiCategory);
      }
    }
    return groups;
  }, new Map<string, typeof highlights>());
  grouped.forEach((items) => items.sort((a, b) => a.student.name.localeCompare(b.student.name, 'pt-BR')));
  const selectedMultiStudent = selectedMultiStudentId ? data.students.find((item) => item.id === selectedMultiStudentId) : null;
  const selectedAchievements = selectedMultiStudent ? data.results.filter((item) => item.published && item.studentId === selectedMultiStudent.id && item.award.trim()).map((result) => {
    const olympiad = data.olympiads.find((item) => item.id === result.olympiadId);
    const phase = olympiad?.phases.find((item) => item.id === result.phaseId);
    return olympiad ? { result, olympiad, phase } : null;
  }).filter((item): item is NonNullable<typeof item> => Boolean(item)) : [];
  const hasActiveFilters = Boolean(query.trim()) || olympiadFilter !== 'all' || classFilter !== 'all' || typeFilter !== 'all' || medalFilter !== 'all';

  return (
    <div className="highlights-wall-page">
      <PageHeader
        eyebrow="Talentos da nossa escola"
        title="Mural de Destaques"
        description={`Conquistas publicadas da unidade ${data.institution.activeUnit || 'em uso'}, celebradas por toda a comunidade escolar.`}
      />

      <section className="highlights-hero" aria-labelledby="highlights-hero-title">
        <div className="highlights-hero-copy">
          <span className="highlights-spark" aria-hidden="true"><Sparkles size={28} /></span>
          <p className="eyebrow light">Orgulho que inspira</p>
          <h2 id="highlights-hero-title">Cada conquista merece seu lugar de destaque.</h2>
          <p>Medalhistas e classificados para novas fases representam dedicação, curiosidade e a coragem de ir além.</p>
        </div>
      </section>

      <section className="highlights-toolbar" aria-label="Filtros do mural">
        <SearchField value={query} onChange={setQuery} placeholder="Buscar aluno, turma ou olimpíada..." label="Buscar no mural" />
        <div className="highlights-select-filters">
          <label><span>Olimpíada</span><select value={olympiadFilter} onChange={(event) => setOlympiadFilter(event.target.value)}><option value="all">Todos</option>{olympiadOptions.map((item) => <option key={item.id} value={item.id}>{item.acronym} · {item.name}</option>)}</select></label>
          <label><span>Turma</span><select value={classFilter} onChange={(event) => setClassFilter(event.target.value)}><option value="all">Todos</option>{classOptions.map((item) => <option key={item} value={item}>{formatClassName(item)}</option>)}</select></label>
          <label><span>Tipo</span><select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value as HighlightFilter)}><option value="all">Todos</option><option value="medalists">Medalhistas</option><option value="qualified">Próximas fases</option></select></label>
          <label><span>Medalha</span><select value={medalFilter} onChange={(event) => setMedalFilter(event.target.value)}><option value="all">Todos</option><option value="multi">Multimedalhista</option><option value="diamond">Diamante</option><option value="platinum">Platina</option><option value="gold">Ouro</option><option value="silver">Prata</option><option value="bronze">Bronze</option><option value="honor">Honra ao Mérito</option></select></label>
        </div>
      </section>

      {highlights.length ? (
        <div className="highlights-classes" aria-live="polite">
          {highlightCategories.filter((category) => grouped.has(category.id)).map((category) => {
            const categoryHighlights = grouped.get(category.id) ?? [];
            const isCollapsed = collapsedCategories.has(category.id);
            const categoryContentId = `highlight-category-${category.id}`;
            return (
            <section className="highlight-class" key={category.id}>
              <div className="highlight-class-heading">
                <button
                  type="button"
                  className="highlight-category-toggle"
                  aria-expanded={!isCollapsed}
                  aria-controls={categoryContentId}
                  onClick={() => setCollapsedCategories((current) => {
                    const next = new Set(current);
                    if (next.has(category.id)) next.delete(category.id);
                    else next.add(category.id);
                    return next;
                  })}
                >
                  <span><span className="eyebrow">{category.label}</span><strong>{category.title}</strong></span>
                  <ChevronDown size={20} aria-hidden="true" />
                </button>
                <span>{new Set(categoryHighlights.map((item) => item.student.id)).size} aluno(s)</span>
              </div>
              <div className="highlight-card-grid" id={categoryContentId} hidden={isCollapsed}>
                {categoryHighlights.map(({ result, student, olympiad, phase }) => {
                  const awardTone = getAwardTone(result.award);
                  const isMultiMedalist = category.id === 'multi';
                  const multiOlympiads = isMultiMedalist ? data.results.filter((item) => item.published && item.studentId === student.id && item.award.trim()).map((item) => data.olympiads.find((olympiad) => olympiad.id === item.olympiadId)?.acronym).filter(Boolean) : [];
                  return (
                    <article className={`highlight-card ${isMultiMedalist ? 'is-multi' : awardTone}`} key={result.id}>
                      {isMultiMedalist ? <button className="highlight-card-trigger" type="button" aria-label={`Ver medalhas de ${student.name}`} onClick={() => setSelectedMultiStudentId(student.id)}><span className="sr-only">Ver todas as conquistas de {student.name}</span></button> : null}
                      <span className="highlight-card-star" aria-hidden="true"><Star size={17} fill="currentColor" /></span>
                      <div className="highlight-student">
                        <span className="avatar">{student.initials}</span>
                        <div><h3>{student.name}</h3><p>{formatClassName(student.className)}</p></div>
                      </div>
                      <div className="highlight-achievement">
                        <span className="highlight-icon" aria-hidden="true">{result.award ? <Medal size={25} /> : <Award size={25} />}</span>
                        <div>
                          <p className="eyebrow">{isMultiMedalist ? 'Multimedalhista' : result.award ? 'Medalha' : 'Classificação'}</p>
                          <strong>{isMultiMedalist ? `${medalCountByStudent.get(student.id)} conquistas publicadas` : result.award || result.classification || 'Classificado para a próxima fase'}</strong>
                          <small>{isMultiMedalist ? `Olimpíadas: ${[...new Set(multiOlympiads)].join(', ')}` : `${olympiad.acronym} · ${olympiad.name}${phase ? ` · ${phase.title}` : ''}`}</small>
                          <div className="highlight-tags">
                            {isMultiMedalist ? <span><Medal size={14} /> Legado de conquistas</span> : null}
                            {result.qualifiedNextPhase ? <span><Star size={14} /> Classificado para a próxima fase</span> : null}
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
              {category.id === 'multi' && selectedMultiStudent ? <Dialog open title={`Conquistas de ${selectedMultiStudent.name}`} description={`${formatClassName(selectedMultiStudent.className)} · ${selectedAchievements.length} medalhas publicadas`} onClose={() => setSelectedMultiStudentId(null)} size="large" centered className="medalist-dialog"><div className="medalist-achievement-grid">{selectedAchievements.map(({ result, olympiad, phase }, index) => <article className={`medalist-achievement ${getAwardTone(result.award)}`} key={result.id} style={{ animationDelay: `${index * 55}ms` }}><span className="highlight-icon" aria-hidden="true"><Medal size={23} /></span><div><p className="eyebrow">{result.award}</p><strong>{olympiad.name}</strong><small>{olympiad.acronym}{phase ? ` · ${phase.title}` : ''}</small></div></article>)}</div></Dialog> : null}
            </section>
          );})}
        </div>
      ) : (
        <EmptyState
          icon={<Trophy />}
          title={hasActiveFilters ? 'Nenhum destaque encontrado' : 'O mural aguarda as primeiras conquistas'}
          description={hasActiveFilters ? 'Ajuste a busca ou os filtros para ampliar os resultados.' : 'Medalhas e classificações aparecerão aqui depois que os resultados forem publicados pela escola.'}
        />
      )}
    </div>
  );
}

function getAwardCategoryId(award: string, qualifiedNextPhase: boolean) {
  const normalized = award.toLocaleLowerCase('pt-BR');
  if (normalized.includes('diamante')) return 'diamond';
  if (normalized.includes('platina')) return 'platinum';
  if (normalized.includes('ouro')) return 'gold';
  if (normalized.includes('prata')) return 'silver';
  if (normalized.includes('bronze')) return 'bronze';
  if (normalized.includes('honra ao mérito')) return 'honor';
  if (award) return 'other';
  return qualifiedNextPhase ? 'qualified' : 'other';
}

function getAwardTone(award: string) {
  const normalized = award.toLocaleLowerCase('pt-BR');
  if (normalized.includes('platina')) return 'award-platinum';
  if (normalized.includes('diamante')) return 'award-diamond';
  if (normalized.includes('ouro')) return 'award-gold';
  if (normalized.includes('prata')) return 'award-silver';
  if (normalized.includes('bronze')) return 'award-bronze';
  if (normalized.includes('honra ao mérito')) return 'award-honor';
  return award ? 'award-medal' : 'award-qualified';
}
